import { setCookie, getCookie } from '@/services/cookie'
import session from '@/services/session'
import {acceptBroadcastMessage} from '@/services/broadcastChannel/broadcastChannelUtils'
import { sendUserMessage, sendWebhookEvent } from '@/services/webhook/webhook'
import { GlobalVars } from '@/services/globals'
import AppWarn from '@/services/errors/app-warns'
import { userMessageParser } from '@/services/parser/userMessageParser'
import { botResponseMessageParser } from '@/services/parser/botResponseParser'
import Storage from '@/services/storageLib'
import cloneDeep from 'lodash/cloneDeep'
import { setTimeOutForTypingIndicator, checkBackToBotTriggered, captureInfoLogs } from '@/services/liveAgent/utils/utils'
import { hasConversationInitiated, getIdleTimeout, isTypeBOT, isUserLoggedIn, extractQuickReplyOptions, checkForGreetingsAndResetCookie, 
  checkRenewTokenRequired, generateAttributes, requestFormatter, isAgentDeskEvent, focusOnChatInputField, setNewUnreadMessagesText, updateUnreadIndicator, isResponseBackendStreaming, getDataInChunks, getSupportIconWidthAndHeight } from '@/services/utils'
import { constantStrings, cookieConstant, chatEvents } from '@/services/constants'
import state from '../state'
import store from '..'
import mqtt from '../../services/aws-iot/mqtt'
import { clearAllSetTimeout, addCustomErrors, handleIfConversationIdNull, extractNBAOptions, clearTimerForInputFieldTemporaryDisable, handleScrollToLastUserMessage, emitOauthEvents, emitEventToParentWindow, clearTimerForInputFieldOnSendDisableTimer, isMultiSourceLastExtension} from '../../services/utils'
import { getUrlData } from '../../services/urlDetails/urlInfo'
import AppLog from '@/services/errors/app-logs'
import { showMessageSeparatorPill } from '@/services/utils';
import { eventType } from '../../services/constants'
import { sendUserMessageForPromptTest , responseAnalyzerSendUserMessage} from '../../services/webhook/webhook'
import VoiceState from '../../services/voiceConstants'
import audioUtil from '../../services/audioUtil'
import TextAccumulatorUtil from '../../services/textAccumulator'
import streamHandler from '../../services/streamHandler'
import { customPayloadTypes } from '../../services/constants'
import ElevenLabsTextToSpeech from '@/services/11labs.js'
import { getShortId, maskSensitiveData } from '../../services/crypto/index'
import { showTermsAndConditionAcceptMessage } from '@/services/parser/eventParser/eventInfo'
import { sendAnalyticsEvent } from '@/services/sendAnalytics'
import { AUTH_CONSTANTS } from '@/constants/authentication'

const TYPING_INDICATOR_TIMER = setTimeOutForTypingIndicator() // Convert this into a class later

export default {
    syncUserMessageFromIoT({ commit }, data) {
      commit('UPDATE_CHATS', data)
    },

    async sendEvent({ dispatch, rootState }, data) {
      try {
        handleIfConversationIdNull(`ConversationId invalid error is received from events API, event data: ${JSON.stringify(data)}`, false)
        const CUSTOM_ATTRIBUTES = generateAttributes({ ...data, generatedBy: data?.userMessage?.generatedBy || constantStrings.GENERATED_BY_SYSTEM })
        const requestBody = {
          triggerType: chatEvents.EVENT,
          eventData: data.eventData,
          conversationId: data.conversationId || getCookie(cookieConstant.SESSION.CONVERSATION_ID),
          botReferenceId: rootState.botRefId,
          requestType: constantStrings.NETOMI,
          channelId: constantStrings.NETOMI_WEB_WIDGET,
          eventName: data.eventName || chatEvents.INFO_PILL,
          agentDesk: data.agentDesk,
          timestamp: data.timestamp || Date.now(),
          isPublishToMQTT: data.isPublishToMQTT || false,
          additionalAttributes: {
            CUSTOM_ATTRIBUTES
          }
        }
        const eventResponse = await sendWebhookEvent({botRefId :rootState.botRefId ,requestBody})
        rootState.widgetConfig?.isAuthenticationEnabled && checkRenewTokenRequired(eventResponse.data, dispatch)
      } catch (err) {
        throw new AppWarn({
          name: 'Action',
          message:`Error while sending event: ${err.message}`,
          error: addCustomErrors(err) || {}
        })
      } finally {
        // cw-accessibility
        document.getElementById('select-feedback-button-alert')?.setAttribute('aria-label', '')
      }
    },

    async sendMessage({ commit, dispatch, rootState }, data) {
      try {
          state.clearConversationState.status === 'pending' && dispatch('handleClearConversationStatus', rootState.widgetConfig?.endChatClearConversation)
          dispatch('updateClearConversationToPending', '')
          dispatch('updateTriggerMinimizeValidState', false)
          const hasChatStarted = hasConversationInitiated()
          const idleTimeout = getIdleTimeout()
          const isEndGreeting = data?.isEndGreeting
          if(!hasChatStarted && !isEndGreeting) {
            setCookie(cookieConstant.SESSION.CONVERSATION_INITIATED, true, {'max-age': idleTimeout / 1000})
            const lastConversationCreatedAt = session.createdAt
            const delay = (getIdleTimeout()) - (new Date().getTime() - lastConversationCreatedAt) // set timer for the remaining time left from idleTimeout
            dispatch('setEndChatTimer', {delay, isGuest: !isUserLoggedIn()}) // start the idle timer on page refresh
            dispatch('setIdleTimerForSurvey') // set idle timer for survey form triggering
            dispatch('overlayIdleChatTimeout')
            // hide surveys of previous conversations
            dispatch('hideSurveyOfPreviousConversation')
            if (rootState.widgetConfig.persistentSurveyConfig?.isPersistentSurveyEnabled) {
              dispatch('updateCSATState', { isEnabled: true })
              dispatch('sendIoTEvent',{event_name: 'CSAT_VISIBILITY', data: true})
            }
          }
          rootState.totalDelay = 0
          if(!isEndGreeting) {
            dispatch('clearItemsBeforeMessageParsing')
          }
          await dispatch('sendIoTEvent',{event_name: 'CLEAR_ITEMS_BEFORE_PARSING'})
          if(rootState.inputFieldTempDisableStatus) {
            clearTimerForInputFieldTemporaryDisable()
            const disableInputFieldTimeout = Number(rootState.clientConfig?.disableInputFieldTimeout || 30000)
            const payload = {
              eventData: {
                eventType: chatEvents.ENABLE_INPUT_FIELD,
                subType: chatEvents.ENABLE_CHAT_INPUT_FIELD,
              },
              eventName: chatEvents.INFO
            }
            const timerId = setTimeout(async()=>{
              dispatch('updateInputFieldTempDisableStatus', false)
              await dispatch('sendIoTEvent',{event_name: 'INPUT_FIELD_TEMPORARY_DISABLE', data: false})
              dispatch('sendEvent', payload)
            },disableInputFieldTimeout)
              dispatch('updateInputFieldTempDisableStatusTimer', timerId)
          }
          const { user } = rootState
          const userMessage = data.userMessage
          session.getConversationId(rootState.widgetConfig) || getCookie(cookieConstant.SESSION.CONVERSATION_ID)
          if(rootState.termsAndConditionsAccepted) {
            const timestamp = Date.now()
            await dispatch('sendEvent', {
              timestamp,
              eventData: {
                  eventType: chatEvents.WIDGET_EVENT,
                  subType: eventType.TERMS_AND_CONDITION_ACCEPTED
              }
            })
            sendAnalyticsEvent(timestamp, rootState.botRefId)
            rootState.termsAndConditionsAccepted = false
            showTermsAndConditionAcceptMessage(null, timestamp)
          }
          const isAppStateOnly = data.isAppStateOnly || false
          const updateLastChatBlock = data.updateLastChatBlock || false
          validateRequiredFieldsSendMessage(user.id, userMessage, data.attachments)
          const CUSTOM_ATTRIBUTES = generateAttributes({ ...data, generatedBy: data?.userMessage?.generatedBy || constantStrings.GENERATED_BY_SYSTEM })
          const requestBody = {
            ...requestFormatter(rootState, data),
            additionalAttributes: {
              CUSTOM_ATTRIBUTES
            }
          }
          if (store.getters.isPromptTestingEnable && !rootState.promptTestDetails?.passCustomAttributes) {
            delete requestBody.additionalAttributes
          }
          const { messagePayload } = requestBody
          const { text = "", label, isLLMSchemaForm } = messagePayload
          const botPCIPolicy = rootState.botPCIPolicy || {}
          const shouldMask = isSensitiveDataMaskingEnabled(rootState) && !text.startsWith('event://;') && !isLLMSchemaForm
          let maskedRequestBody = requestBody
          if (shouldMask) {
            const maskedText = maskSensitiveData(botPCIPolicy, text)

            const maskedLabel = (text !== label)
              ? maskSensitiveData(botPCIPolicy, label)
              : maskedText

            if (maskedText !== text || maskedLabel !== label) {
              maskedRequestBody = cloneDeep(requestBody)
              maskedRequestBody.messagePayload.text = maskedText
              maskedRequestBody.messagePayload.label = maskedLabel

              commit('UPDATE_MASKED_VALUE_MAP', {
                messageId: maskedRequestBody.messagePayload.messageId,
                text,
                maskedText,
                label,
                maskedLabel
              })
            }
          }
          //Sending message across Tabs for user message sync
          acceptBroadcastMessage({
            ...(shouldMask ? maskedRequestBody : requestBody),
            updateLastChatBlock
          })
          const messageBody = userMessageParser(shouldMask ? maskedRequestBody : requestBody)
          if(isAppStateOnly) messageBody.isAppStateOnly = isAppStateOnly
          if(data.hide) messageBody.messages[0].hide = true

          let unmaskedUserQuery = requestBody?.messagePayload?.label
          if (store.getters.isResponseAnalyzerEnabled) {
            const messageId = requestBody?.messagePayload?.messageId
            const responseAnalyzerMsgId = `resp_analyser_${messageId}`
            requestBody.messagePayload.messageId = responseAnalyzerMsgId
            if (rootState.promptTestDetails?.userDetails) {
              requestBody.userDetails = rootState.promptTestDetails.userDetails
            }
            unmaskedUserQuery = getUnmaskedString(requestBody?.messagePayload?.label, rootState)
          }

          dispatch('showDayDateSeparator', requestBody)
          if (updateLastChatBlock) {
            commit('REPLACE_LAST_IMAGE_SKELETON', messageBody)
          } else {
            commit('UPDATE_CHATS', messageBody)
          }
          if(!data.hide) {
            streamHandler.init(true)
            TextAccumulatorUtil.init()
          }
          const messageId = requestBody?.messagePayload?.messageId
          const messageKeyForLocalStorage = store.getters.isResponseAnalyzerEnabled ? 'responseAnalyzerMessageId' : 'currentUserMessageId'
          Storage.setLocalStorageData(messageKeyForLocalStorage, messageId)
          if(rootState.isVoiceModeActive && !data.hide) {
            rootState.currentVoiceState = VoiceState.SENDING
          }
          dispatch('throttleFallbackMechanism', { ...data, timestamp: messageBody.timestamp })
          
          if (isTypeBOT(state.controlType) && store.getters.disableInputFieldOnSendMessage) {
            dispatch('updateInputFieldOnSendMessage', true)
            await dispatch('sendIoTEvent',{event_name: 'INPUT_FIELD_DISABLE_ON_SEND', data: true})
            const defaultTypingTimeout = store.getters.timeoutInputFieldOnSendMessage;
            const typingTimeout = data.hideTypingIndicator ? 0 : defaultTypingTimeout;
            store.state.inputFieldOnSendDisableTimer = setTimeout(async () => {
              dispatch('updateInputFieldOnSendMessage', false)
              await dispatch('sendIoTEvent',{event_name: 'INPUT_FIELD_DISABLE_ON_SEND', data: false})
            }, typingTimeout)
          }
          !isAppStateOnly && await dispatch('sendIoTMessage', messageBody)
          let userMessageReq
          if (store.getters.isPromptTestingEnable) {
            emitEventToParentWindow(eventType.USER_QUERY_SENT,{ conversationId: requestBody?.conversationId, userQuery: unmaskedUserQuery, messageId: requestBody?.messagePayload?.messageId})
            const userMessageMethod = store.getters.isResponseAnalyzerEnabled ? responseAnalyzerSendUserMessage : sendUserMessageForPromptTest
            userMessageReq = !isAppStateOnly && await userMessageMethod({ botId: rootState.promptTestDetails?.botId, botRefId: rootState.botRefId, socialChannel: rootState.promptTestDetails?.socialChannel, requestBody })
          } else {
            userMessageReq = !isAppStateOnly && await sendUserMessage({ botRefId: rootState.botRefId, requestBody })
          }
          rootState.widgetConfig?.isAuthenticationEnabled && userMessageReq && checkRenewTokenRequired(userMessageReq.data, dispatch)
          if(!isTypeBOT(state.controlType)){
            GlobalVars.$liveAgent.sendMessageToLiveAgent(requestBody)
          }
          if(rootState.isVoiceModeActive && !data.hide) {
            audioUtil.endAudio()
            rootState.currentVoiceState = VoiceState.NONE
          }
          handleIfConversationIdNull('ConversationId invalid error is received from send message API', true)
        } catch (err) {
          clearTimerForInputFieldOnSendDisableTimer()
          store.dispatch('updateInputFieldOnSendMessage', false)
          await dispatch('sendIoTEvent',{event_name: 'INPUT_FIELD_DISABLE_ON_SEND', data: false})
          dispatch('updateTypingIndicatorStatus', false)
          // If message is a greeting, set cookie to false to retry again if user comes online from offline, also don't throw app error
          if (checkForGreetingsAndResetCookie(data)) {
            dispatch('clearChats')
            return
          }
          const errorMessage = {
            errMessage: err.message,
            argument: data,
            controlType: state.controlType
          }
          throw new AppWarn({
            name: 'Action',
            message:`Error while sending message: ${JSON.stringify(errorMessage)}`,
            error: addCustomErrors(err) || {}
          })
        } finally {
          handleIfConversationIdNull(`ConversationId invalid error is received from send message API with data: ${JSON.stringify(data)}`, true)
          dispatch('updateShowFormControls', false)
          if (isTypeBOT(rootState.controlType)) {
            TYPING_INDICATOR_TIMER.clearTimer()
            if (store.getters.isTypingIndicatorEnabled && !data.hideTypingIndicator) dispatch('updateTypingIndicatorStatus', true)
            const minTimeForTypingIndicator = rootState.widgetConfig.typingIndicator.minTime || constantStrings.DEFAULT_MIN_TIME_FOR_TYPING_INDICATOR
            rootState.typingIndicatorTimeoutPromise = new Promise((resolve) => {
              setTimeout(() => {
                resolve(constantStrings.TYPING_INDICATOR_TIMEOUT)
              }, minTimeForTypingIndicator);
            })
            TYPING_INDICATOR_TIMER.startTimer()
          }
        }
    }, 

    processEvent({ commit, dispatch, rootState }, data) {
      if (data?.eventObject?.eventData?.eventType === chatEvents.SECURE_FORM) {
          dispatch('handleSecureForm', data?.eventObject?.eventData)
          return
      }
      
      if (isAgentDeskEvent(data?.eventObject)) {
        dispatch('receiveAgentDeskEvents', data)
        return
      }

      if (data?.eventObject?.eventData?.eventType === AUTH_CONSTANTS.OAUTH) {
        const subType = data?.eventObject?.eventData?.subType
        const handler = eventHandlers[subType]
        if (handler) {
          handler({ commit, dispatch, rootState }, data)
        }
      }
    },

    async processResponse({ commit, dispatch, rootState }, data) {
      new AppLog({
        name: 'Process Response Function', 
        message: data, 
        logLevel: 'info', 
        service: 'Action'
      })
      if(handleAggregatedChunk(data)){
        return
      }
      setNewUnreadMessagesText(data) // set new unread messages for accessibility
      updateUnreadIndicator(true)
      const currentMessageLocale = data?.customPayload?.[customPayloadTypes.TRANSLATE_LANGUAGE]
      const lastMessageLocale = store.state.lastMessageLocale
      if (currentMessageLocale !== lastMessageLocale) {
        store.state.lastMessageLocale = currentMessageLocale
        ElevenLabsTextToSpeech.closeConnection()
        audioUtil.initElevenLabsWebSocket()
      }
      if (!isTypeBOT(data.ownerType)) {
        if(store.state.inputFieldOnSendDisableTimer) {
          clearTimerForInputFieldOnSendDisableTimer()
          dispatch('updateInputFieldOnSendMessage', false)
        }
        dispatch('receiveAgentDeskMessages', data)
        return
      }
      if (!isResponseBackendStreaming(data) && data?.attachments?.length) {
        dispatch('clearFallbackMechanismTimer')
      }
      const isBackToBotTriggered = checkBackToBotTriggered(data.ownerType)
      const isTypingIndicatorTimedout = await rootState.typingIndicatorTimeoutPromise
      TYPING_INDICATOR_TIMER.clearTimer()
      if(!isBackToBotTriggered && rootState.typingIndicatorTimeoutPromise && isTypingIndicatorTimedout !== constantStrings.TYPING_INDICATOR_TIMEOUT) { 
        return
      }
      if(isResponseBackendStreaming(data)) { // chunks streaming from backend
        streamHandler.addAndProcessChunks(data)
        return
      }
      const botResponse = botResponseMessageParser(data, true, false)
      const messages = cloneDeep(botResponse.messages)
      messages?.length && clearTimerForInputFieldTemporaryDisable()
      const delay = rootState.widgetConfig.timeDelayBetweenMessageBubbles
      let totalDelay
      const timerIds = []
      if(messages.length > 0) {
        const isLastMessageExtension = data?.customPayload?.isLastMessageExtension || false
        store.getters.isPromptTestingEnable && emitEventToParentWindow(eventType.RESPONSE_RECEIVED,{conversationId: data?.requestPayload?.conversationId, messageId:data?.requestPayload?.messagePayload?.messageId, requestId: data?.requestPayload?.requestId, userQuery: data?.requestPayload?.messagePayload?.label})
        if(store.getters.isStreamingEnabled && !isLastMessageExtension) { // streming and animation handled on FE only
          if(store.getters.disableInputFieldWhileStreaming) {
            dispatch('updateInputFieldStreamingDisableStatus', true)
          }
         totalDelay = updateChatsWithResponseWithStreaming({ commit, dispatch, rootState }, botResponse, messages, delay, timerIds, streamHandler.getTotalDelay())
         const id = setTimeout(() => {
          if(store.state.isVoiceModeActive) {
            const audioMessage = messages.find(msg => msg.type === 'AudioResponse')
            if (audioMessage?.messageValue?.text && store.getters.textToSpeechOrationResponse) {
              TextAccumulatorUtil.accumulateText(audioMessage.messageValue.text)
            }
            TextAccumulatorUtil.playLastChunk();
          }
          dispatch('updateInputFieldStreamingDisableStatus', false)
          clearTimerForInputFieldOnSendDisableTimer()
          dispatch('updateInputFieldOnSendMessage', false)
         }, totalDelay);
         timerIds.push(id)
         rootState.totalDelay = totalDelay
        } else {
          if (isLastMessageExtension) {
            const extensionMessageTimerId = setTimeout(() => {
              rootState.totalDelay = 0
              clearTimerForInputFieldOnSendDisableTimer()
              dispatch('updateInputFieldOnSendMessage', false)
              updateChatsOfLastIndex({ commit, dispatch, rootState }, messages, delay, timerIds)
            }, Math.max(rootState.totalDelay, streamHandler.getTotalDelay()))
            timerIds.push(extensionMessageTimerId)
          } else {
            // update chats with bot response
            const chatResponseDeplay =  updateChatsWithResponse({ commit, dispatch, rootState }, botResponse, messages, delay, timerIds, streamHandler.getTotalDelay())
            const id = setTimeout(() => {
              clearTimerForInputFieldOnSendDisableTimer()
              dispatch('updateInputFieldOnSendMessage', false)
             }, chatResponseDeplay)
             timerIds.push(id)
             rootState.totalDelay = totalDelay
          }
        }
        // end chat after rendering all bot messages
        rootState.endChatCustomFieldStatus && setEndChatTimer({ dispatch }, delay, messages, totalDelay)
        // handle back to bot trigger
        isBackToBotTriggered && dispatch('handleBackToBotTrigger')
        //handleNBAResponse
        fetchNBAResponses({rootState, dispatch}, delay, messages, timerIds, totalDelay)
        // Fetch Quick Replies, set Typing indicator to false
        if (!(isMultiSourceLastExtension(data,messages))) {
          fetchQuickReplies({rootState, dispatch}, delay, messages, timerIds, totalDelay)
        }
      }
    },

    receiveAWSIoTMessage({ dispatch }, data) {
      try {
        const triggerType = data.triggerType
        switch (triggerType) {
          case chatEvents.EVENT:
            dispatch('processEvent', data)
            break;
          case chatEvents.RESPONSE:
            dispatch('processResponse', data)
            break;
        }
      } catch (err) {
        const errorMessage = {
          errMessage: err.message,
          argument: data
        }
        new AppWarn({
          name: 'Action',
          message:`Error while receiving message: ${JSON.stringify(errorMessage)}`,
          error: addCustomErrors(err) || {}
        })
      }
    },

    throttleIdleChatTimeout({ dispatch, rootState }) {
        session.updateConversation(rootState.widgetConfig) // extend the expiry for conversationId and createdAt cookie
        const delay = getIdleTimeout()
        dispatch('setEndChatTimer', {delay, isGuest: !isUserLoggedIn()})
        // throttle AgentDesk Connection
        dispatch('throttleAgentDeskConnectionIdleTimeout', store.getters.getAgentDeskIdleTimeout)
    },

    async clearItemsBeforeMessageParsing({ dispatch }) {
      dispatch('updateQuickReplyOptions', []) // Clear Quick Reply Options if any
      dispatch('updateActionableNBAOptions', [])
      dispatch('updateNonActionableNBAOptions', [])
      dispatch('throttleIdleChatTimeout') // Throttle chat session timeout
      await dispatch('setIdleTimerForSurvey') // set idle timer for survey form triggering
      dispatch('overlayIdleChatTimeout') // User inactive overlay timeout
      dispatch('updateEndChatModalStatus', false) // Close endChat modal
    },

    async sendIoTEvent(_props, {event_name, data}) {
      await mqtt.publishToIoT({
        isEvent: true,
        tabId: Storage.getSessionStorageData('tabId'),
        event_type: event_name,
        payload: data
      })
    },

    async sendIoTMessage(_props, messageBody) {
      await mqtt.publishToIoT(messageBody)
    },

    async enableTempDisabledInputField({ dispatch}) {
      const payload = {
        eventData: {
          eventType: chatEvents.ENABLE_INPUT_FIELD,
          subType: chatEvents.ENABLE_CHAT_INPUT_FIELD,
        },
        eventName: chatEvents.INFO
      }
      dispatch('updateInputFieldTempDisableStatus', false)
      await dispatch('sendIoTEvent',{event_name: 'INPUT_FIELD_TEMPORARY_DISABLE', data: false})
      dispatch('sendEvent', payload)
    },

    handleBackToBotTrigger ({ dispatch }, attributes) {
      captureInfoLogs('Resume bot', 'Bot is resumed')
      dispatch('updateControlType', constantStrings.BOT)
      dispatch('throttleIdleChatTimeout') // Throttle chat session timeout for agentDesk
      dispatch('setIdleTimerForSurvey') // set idle timer for survey form triggering
      GlobalVars.$liveAgent.endChatWithLiveAgent(attributes)
    },

    processChunkResponse ({ commit, dispatch, rootState }, data) {
      const botResponse = botResponseMessageParser(data, true, false)
      const messages = cloneDeep(botResponse.messages)
      if(messages.length > 0) {
        updateChatsWithResponse({ commit, dispatch, rootState }, botResponse, messages, 0, [])
        const isBackToBotTriggered = checkBackToBotTriggered(data.ownerType)
        isBackToBotTriggered && dispatch('handleBackToBotTrigger')
      }
    },

    processQuickReplies ({dispatch, rootState},{delay,messages,timerIds}) {
      fetchQuickReplies({rootState, dispatch}, delay, messages, timerIds)
    },

    removeStreamingFromAttachment({commit}, data) {
      data.attachment.text = ""
      data.attachment.isStreaming = false
      data.attachment.isPartialStreaming = false
      commit('UPDATE_TEXT_CHUNK_MESSAGE', data)
    },

    addFeedbackforLastAttachment({commit}) {
      commit('ADD_FEEDBACK_FOR_LAST_ATTACHMENT')
    },

    updateMessageInLastChat({commit}, data) {
      commit('UPDATE_LAST_CHAT_MESSAGE', data)
    },
    async sendQuickReplyMessage({rootState, dispatch}, data){
      const isValid = handleAlertsAndTerms(data, rootState, dispatch)
      if(isValid) return
      await dispatch('sendMessage', data)
    }
}

export const isSensitiveDataMaskingEnabled = (rootState) => {
    return rootState.widgetConfig?.isSensitiveDataMaskingEnabled || false
}

const validateRequiredFieldsSendMessage = (
    userId,
    userMessage,
    attachments
  ) => {
    if (!userId) throw Error('User Id not generated properly')
    if (!userMessage && !attachments?.length)
      throw Error('Please type valid message or attachment')
}

const updateChatsOfLastIndex = ({ commit, dispatch }, messages, delay, timerIds) => {
  messages?.forEach((message, index) => {
    const id = setTimeout(() => {
      /* Clear all the instance of messages from the Microtask queue if end chat initiated */
      if(!getCookie(cookieConstant.SESSION.CONVERSATION_ID)) return clearAllSetTimeout(timerIds)
      if (index === messages.length-1) {
        dispatch('updateTypingIndicatorStatus', false)
      } else {
        if (store.getters.isTypingIndicatorEnabled) dispatch('updateTypingIndicatorStatus', true)
        updateUnreadIndicator(true)
      }
      commit('UPDATE_LAST_CHAT_MESSAGE', message)
    }, delay * index)
    timerIds.push(id)
  })
  setNewUnreadMessagesText() // unset new unread messages text
  setFocusAfterMessagesDisplay(delay * messages.length - 1)
}

const updateChatsWithResponse = ({ commit, dispatch }, botResponse, messages, delay, timerIds, additionalDelay) => {
  botResponse.messages = []
  commit('UPDATE_CHATS', botResponse) // Make empty template in chat thread
  // Insert Messages with Delay * index
  let totalDelay = 0
  messages.forEach((message, index) => {
    const timeoutDelay = (delay * index) + additionalDelay
    const id = setTimeout(() => {
      /* Clear all the instance of messages from the Microtask queue if end chat initiated */
      if(!getCookie(cookieConstant.SESSION.CONVERSATION_ID)) return clearAllSetTimeout(timerIds)
      if (index === messages.length-1) {
        dispatch('updateTypingIndicatorStatus', false)
      } else {
        if (store.getters.isTypingIndicatorEnabled) dispatch('updateTypingIndicatorStatus', true)
        updateUnreadIndicator(true)
      }
      if (isLastTextMessage(messages, index)) {
        message.messageValue.isLastChunk = true
      }
      commit('UPDATE_LAST_CHAT_MESSAGE', message)
    }, timeoutDelay)
    totalDelay += timeoutDelay
    timerIds.push(id)
  })

  setNewUnreadMessagesText() // unset new unread messages text
  setFocusAfterMessagesDisplay(delay * messages.length - 1)
  return totalDelay
}

const updateChatsWithResponseWithStreaming = ({ commit, dispatch }, botResponse, messages, delay, timerIds, additionalDelay) => {
  const audioMessageFound = messages.some(msg => msg.type === 'AudioResponse')
  //handle streaming and animation on FE onlt
  botResponse.messages = []
  commit('UPDATE_CHATS', botResponse) // Make empty template in chat thread
  // Insert Messages with Delay * index
  let previousDelay = additionalDelay
  messages?.forEach((message, index) => {
    message.messageValue.isStreaming = true
    if(store.getters.textToSpeechOrationResponse) {
      message.audioMessageFound = audioMessageFound
    }
    let delayForEachMessage = 0
    if (index > 0) {
      delayForEachMessage = delay
      const previousMessage = messages[index-1]
      if (previousMessage.type === 'TextResponse') {
        const chunksLength = getDataInChunks(previousMessage?.messageValue?.text)?.length || 0
        const timeTakenForStreaming = constantStrings.STREAM_TIME_FOR_EACH_CHUNK * chunksLength
        delayForEachMessage = Math.max(timeTakenForStreaming, delay)
      }
    }
    const id = setTimeout(() => {
      /* Clear all the instance of messages from the Microtask queue if end chat initiated */
      if(!getCookie(cookieConstant.SESSION.CONVERSATION_ID)) return clearAllSetTimeout(timerIds)
      if (index === messages.length-1) {
        dispatch('updateTypingIndicatorStatus', false)
      } else {
        if (store.getters.isTypingIndicatorEnabled) dispatch('updateTypingIndicatorStatus', true)
        updateUnreadIndicator(true)
      }
      if (isLastTextMessage(messages, index)) {
        message.messageValue.isLastChunk = true
      }
      commit('UPDATE_LAST_CHAT_MESSAGE', message)
    }, delayForEachMessage + previousDelay)
    previousDelay = previousDelay + delayForEachMessage
    timerIds.push(id)
  })
  let delayForEachMessage = 0
  if(messages.length > 1) {      // for only one element already calculated in above forEach
    const message = messages[messages.length - 1]
    if (message.type === 'TextResponse') {
      const chunksLength = getDataInChunks(message?.messageValue?.text)?.length || 0
      const timeTakenForStreaming = constantStrings.STREAM_TIME_FOR_EACH_CHUNK * chunksLength
      delayForEachMessage = Math.max(timeTakenForStreaming, delay)
    }
    const id = setTimeout(() => {
      /* Clear all the instance of messages from the Microtask queue if end chat initiated */
      if(!getCookie(cookieConstant.SESSION.CONVERSATION_ID)) return clearAllSetTimeout(timerIds)
      dispatch('updateTypingIndicatorStatus', false)
    }, delayForEachMessage + previousDelay)
    previousDelay = previousDelay + delayForEachMessage
    timerIds.push(id)
  }
  setNewUnreadMessagesText() // unset new unread messages text
  setFocusAfterMessagesDisplay(previousDelay)
  return previousDelay
}

const setEndChatTimer = ({ dispatch }, delay, messages, totalDelay) => {
  const maxDelay = totalDelay ? totalDelay : delay * messages.length
  setTimeout(() => {
    dispatch('endChat', { message: constantStrings.CHAT_ENDED, publishEventToIoT: false })
    dispatch('updateEndChatCustomFieldStatus', false)
  },maxDelay)
}

const fetchQuickReplies = ({ dispatch }, delay, messages, timerIds, totalDelay) => {
  const maxDelay = totalDelay ? totalDelay : Math.max(delay * (messages.length - 1), 500 + streamHandler.getTotalDelay()) // added for some more delay
  const id = setTimeout(() => {
    dispatch('updateTypingIndicatorStatus', false)
    if(!getCookie(cookieConstant.SESSION.CONVERSATION_ID)) return clearAllSetTimeout(timerIds)
    const quickReplyOptions = extractQuickReplyOptions(messages)
    dispatch('updateQuickReplyOptions', quickReplyOptions)
    handleScrollToLastUserMessage();
    updateUnreadIndicator(true)
  }, maxDelay)
  timerIds.push(id)
  setTimeout(() => {
    focusOnChatInputField(true)
  }, maxDelay+1000)
}

const setFocusAfterMessagesDisplay = (delay) => {
  setTimeout(() => {
    focusOnChatInputField(true)
  }, delay)
}

const fetchNBAResponses = ({ dispatch }, delay, messages, timerIds, totalDelay ) => {
  const maxDelay = totalDelay ? totalDelay : Math.max(delay * (messages.length - 1), 2000 + streamHandler.getTotalDelay()) // added for some more delay
  const nbaOptions = extractNBAOptions(messages)
  let data
  if (Object.keys(nbaOptions)?.length) {
    const { nonActionableNBAOptions } = nbaOptions
    if(nonActionableNBAOptions?.length){
      const urlArr = nonActionableNBAOptions.map(option=>option.url)
      data = getUrlData({requestBody:urlArr})
    }
  }
  const id = setTimeout(async () => {
    dispatch('updateTypingIndicatorStatus', false)
    if(!getCookie(cookieConstant.SESSION.CONVERSATION_ID)) return clearAllSetTimeout(timerIds)
    if (Object.keys(nbaOptions)?.length) {
      const { actionableNBAOptions = [], nonActionableNBAOptions = [] } = nbaOptions
      dispatch('updateActionableNBAOptions', actionableNBAOptions)
      dispatch('updateNonActionableNBAOptions', nonActionableNBAOptions)
      data?.then(info=>{
       const processedNbaOptions = nonActionableNBAOptions?.map((option,index)=>{
        const value= info?.[index]?.value
        const image = value?.image || value?.logo
        const longText = value?.description
        const label = value?.title
        const obj={...option,image,isProcessed:true}
        if(!obj.label && label) {
          obj.label = label
        }
        if(!obj.longText && longText) {
          obj.longText = longText
        }
        return obj
       })
       processedNbaOptions?.length && dispatch('updateNonActionableNBAOptions', processedNbaOptions)
      })?.catch(err=>{
        console.log(err)
      })
    } else {
      dispatch('updateActionableNBAOptions', [])
      dispatch('updateNonActionableNBAOptions', [])
    }
    handleScrollToLastUserMessage();
    updateUnreadIndicator(true)
  }, maxDelay)
  timerIds.push(id)
}


function isLastTextMessage(messages,index) {
  for (let i = messages?.length-1; i >=0; i-- ) {
    if (messages[i]?.type === 'TextResponse' && messages[i]?.messageValue?.text) {
      return index===i
    }
  }
  return false
}


const handleAlertsAndTerms = (data, rootState, dispatch) => {
  const isTermsValid = Object.keys(rootState.termsAndConditions.terms).length
  if (isTermsValid) {
    dispatch('updateQuickReplyOnTermsClosureState', data)
    return true
  }
  return false
}

function signInHandler({ commit, dispatch, rootState }, data) {
  localStorage.setItem(AUTH_CONSTANTS.ISAUTH, true)
  const loggedInUtterence = rootState.widgetConfig?.loggedInUtterence

  showMessageSeparatorPill({
    message: AUTH_CONSTANTS.SUCCESSFULLY_SIGNED_IN,
    isEndChatSeparator: false
  })

  const userDetails = data?.eventObject?.eventData?.userdetails
  if (userDetails) {
    const { authenticatedConversationId } = data.eventObject.eventData
    const externalId = userDetails?.externalId || userDetails?.userId

    commit('UPDATE_EXTERNAL_ID', externalId)
    dispatch('setOauthUserLoggedIn', authenticatedConversationId)
    dispatch('updateTermsAndConditions')

    localStorage.setItem(AUTH_CONSTANTS.USER_DETAILS, JSON.stringify(userDetails))
  }

  emitOauthEvents('SIGNED_IN')
  dispatch('sendProactiveGreetingUtterances', loggedInUtterence)
}

function signOutHandler({ dispatch }) {
  showMessageSeparatorPill({
    message: AUTH_CONSTANTS.SUCCESSFULLY_SIGNED_OUT,
    isEndChatSeparator: false
  })
  emitOauthEvents('SIGNED_OUT')
  dispatch('toggleChatWindowStatus', false)
  emitEventToParentWindow(eventType.UPDATE_PARENT_WINDOW_STYLE, getSupportIconWidthAndHeight())
  dispatch('setOauthlogoutWidget')
}

function signInFailureHandler() {
  showMessageSeparatorPill({
    message: AUTH_CONSTANTS.SIGN_IN_AUTHENTICATION_FAILED,
    isEndChatSeparator: false
  })
  localStorage.setItem(AUTH_CONSTANTS.ISAUTH, AUTH_CONSTANTS.FAIL)
}

function signOutFailureHandler() {
  showMessageSeparatorPill({
    message: AUTH_CONSTANTS.SIGN_OUT_AUTHENTICATION_FAILED,
    isEndChatSeparator: false
  })
}

async function multiSessionSignout ({ dispatch, rootState }, data) {
  const message = data?.eventObject?.eventData?.message
  showMessageSeparatorPill({
    message: message || AUTH_CONSTANTS.SUCCESSFULLY_SIGNED_OUT,
    isEndChatSeparator: false
  })
  emitOauthEvents('SIGNED_OUT')
  await dispatch('endChat', {
    message: constantStrings.CHAT_ENDED, 
    showSessionRestartGreeting: true,
    hideEndChatGreeting: getIsSendTranscriptEnabled(rootState),
    ...(getEndChatModalOnChatRestart(rootState) && { clearConversation: true, hidePill: true, restartChat: true }),
  })
}

function getEndChatModalOnChatRestart (state) {
   return state.showEndChatFlow?.isChatRestart
}

function getIsSendTranscriptEnabled (rootState) {
  const endChatClientConfigsEnabled = rootState?.clientConfigs?.endChat?.enabled
  const endChatClientConfigs = endChatClientConfigsEnabled ? rootState?.clientConfigs?.endChat?.config : null
  
  if (getEndChatModalOnChatRestart(rootState)) {
    return !!rootState.widgetConfig?.restartChat?.showWarning?.isSendTranscriptEnabled;
  }

  if(endChatClientConfigs?.isSendTranscriptEnabled !== undefined) {
    return endChatClientConfigs?.isSendTranscriptEnabled
  }

  return rootState.widgetConfig.endChat
    ? rootState.widgetConfig?.endChat?.isSendTranscriptEnabled
    : true            
}

function getUnmaskedString (maskedString, rootState) {
  const maskedRegex = /masked-[^-]+?-masked/g
  const maskedMatches = maskedString.match(maskedRegex)
  const unmaskedStoreValues = rootState.formInputMaskedValue
  if (maskedMatches?.length) {
    maskedMatches.forEach((match) => {
      const replacement = unmaskedStoreValues[getShortId(match)] || ''
      maskedString = maskedString.replace(match, replacement)
    })
  }
  return maskedString
}

const eventHandlers = {
  [AUTH_CONSTANTS.SIGN_IN]: signInHandler,
  [AUTH_CONSTANTS.SIGN_OUT]: signOutHandler,
  [AUTH_CONSTANTS.SIGN_IN_FAILURE]: signInFailureHandler,
  [AUTH_CONSTANTS.SIGN_OUT_FAILURE]: signOutFailureHandler,
  [AUTH_CONSTANTS.MULTI_SESSION_SIGN_OUT]: multiSessionSignout
}

function handleAggregatedChunk (data) {
  return data?.customPayload?.[customPayloadTypes.IS_CHUNK_AGGREGATED_RESPONSE]
}
